import React from 'react'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import { Box, Typography } from '@mui/material'
import { GUIDANCE_NOTE_DEFAULT_TITLE } from '../constants/guidanceNoteConstants'
import {
  UI_COLOR_ACCENT_ORANGE_SOFT,
  UI_COLOR_SURFACE_TINT,
  UI_COLOR_TEXT_NEUTRAL
} from '../constants/uiColors'

const actionWrapSx = {
  ml: { xs: 0, md: 'auto' },
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center'
}

const GuidanceNote = ({
  title = GUIDANCE_NOTE_DEFAULT_TITLE,
  message,
  children,
  action = null,
  icon = <LightbulbOutlinedIcon />,
  accentColor = UI_COLOR_ACCENT_ORANGE_SOFT,
  backgroundColor = UI_COLOR_SURFACE_TINT,
  textColor = UI_COLOR_TEXT_NEUTRAL,
  singleLine = false,
  role,
  sx = {},
  headerSx = {},
  iconSx = {},
  titleSx = {},
  messageSx = {},
  contentSx = {},
  actionSx = {}
}) => {
  const hasTitle = title !== null && title !== undefined && title !== ''
  const actionNode = action ? (
    <Box sx={{ ...actionWrapSx, ...actionSx }}>{action}</Box>
  ) : null

  return (
    <Box
    role={role}
    sx={{
      border: `1px solid ${accentColor}`,
      borderLeft: `4px solid ${accentColor}`,
      borderRadius: '8px',
      px: {
        xs: 2,
        md: 2.5
      },
      py: 1.5,
      bgcolor: backgroundColor,
      ...sx
    }}
  >
    {singleLine ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          columnGap: 0.5,
          rowGap: 0.25,
          ...headerSx
        }}
      >
        <Box
          sx={{
            color: accentColor,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
            mr: 0.5,
            ml: -0.5,
            flexShrink: 0,
            '& svg': {
              fontSize: 20
            },
            ...iconSx
          }}
        >
          {icon}
        </Box>

        {hasTitle ? (
          <Typography
            sx={{
              color: accentColor,
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: 1.3,
              ...titleSx
            }}
          >
            {title}
          </Typography>
        ) : null}

        {message && (
          <Typography
            sx={{
              color: textColor,
              fontSize: '12.5px',
              lineHeight: 1.5,
              fontWeight: 500,
              flex: actionNode ? { md: 1 } : undefined,
              minWidth: 0,
              ...messageSx
            }}
          >
            {message}
          </Typography>
        )}

        {children}
        {actionNode}
      </Box>
    ) : (
      <Box
        sx={{
          display: 'flex',
          alignItems: actionNode ? 'center' : 'flex-start',
          columnGap: 1,
          ...headerSx
        }}
      >
        <Box
          sx={{
            color: accentColor,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
            mr: 0.5,
            ml: -0.5,
            flexShrink: 0,
            '& svg': {
              fontSize: 20
            },
            ...iconSx
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1, ...contentSx }}>
          {hasTitle ? (
            <Typography
              sx={{
                color: accentColor,
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: 1.3,
                ...titleSx
              }}
            >
              {title}
            </Typography>
          ) : null}

          {message && (
            <Typography
              sx={{
                color: textColor,
                fontSize: '12px',
                lineHeight: 1.5,
                fontWeight: 500,
                mt: hasTitle ? 0.15 : 0,
                ...messageSx
              }}
            >
              {message}
            </Typography>
          )}

          {children}
        </Box>
        {actionNode}
      </Box>
    )}
  </Box>
  )
}

export default GuidanceNote
